import type { ModKey } from './mods';
// 育成・経済の数値はすべてここに集める。シミュレーターや調整用に configure() で上書きできる。
// 各関数は呼ばれた時点の CONFIG を読む（キャッシュは configVersion() で無効化する）。

export const DEFAULT_CONFIG = {
  level: {
    /**
     * 主Lv の上限。2026-10-20 に 100 → 300 → 1000（旧Lv1つを unit=9 個に刻んだ。旧Lv x ＝ 主Lv 9x-8。100面の想定 Lv92 → Lv820）。
     * Lv820〜1000 はハードモード（難易度 壱〜）で伸ばす幅（BALANCE.md 27章）。
     * 2026-11-04：本編（第100陣）クリアまでは maxBeforeClear、クリアで max まで開く（上の難易度で伸び続けるように）。
     */
    max: 1500,
    maxBeforeClear: 1000,
    /** maxBeforeClear より上のLvの必要EXPの倍率（クリア後の伸びを1か月ほどに延ばす） */
    afterClearNeedMult: 3,
    /** 旧Lv（上限100のとき）1つあたりの主Lv の数。必要EXPの式・taper・valley は旧Lv x で書く（x = (主Lv + (unit-1)/2) / unit） */
    unit: 9,
    /**
     * 次のLvまでのEXP = round((needA * x^needP + needC * x + needB) × taper(x) × valley(x) / unit)（x は旧Lv。旧Lv1つ分の累計は旧式と同じ。
     * needB は旧の累計を超えないよう 60 → 58.5）。
     * 以下は旧Lv（上限100）のときの経緯：次のLvまでのEXP = round(needA * L^needP + needC * L + needB)。序盤は安く（Lv1→2 が約120）、Lv50前後からは旧式（3.7L²+800）とほぼ同じ。
     * 初回クリアだけで 1-1 で Lv3・1章で Lv10・2章で Lv15 前後。累計は economy-sim の到達日（EXP2倍で約60日）で合わせる（BALANCE.md 9章）。
     * キャラ別スキル（10章）に替えたとき 3.1L²+54L+60 → 2.8L²+49L+60（Lv50以降の必要量 約-9%）にした。
     * 式神の作り直し（GAME_DESIGN 16章。推奨戦力の想定にお供が入り、★の効果の想定が上がった）で 2.8L²+49L+60 → 2.4L²+42L+60（約-14%）にした
     */
    needA: 2.4,
    needP: 2,
    needC: 42,
    needB: 58.5,
    /** 終盤のLvの必要EXPの割引（旧Lv）：from で1倍 → to で mult 倍（間は直線、to より上も mult）。終盤は一気に育つように（BALANCE.md 17章） */
    taper: { from: 65, to: 90, mult: 0.4 },
    /** 中盤の谷をならす倍率の折れ線 [[旧Lv, 倍率], …]（間は直線、範囲外は1）。1Lvに100戦以上かかっていた旧Lv30〜60を浅くした（BALANCE.md 27章） */
    valley: [[21, 1], [32, 0.7], [43, 0.7], [53, 0.45], [62, 0.5], [77, 0.6], [87, 1]] as [number, number][],
    /** Lvごとの基礎能力（HP・攻撃・防御）の%補正（旧Lv1つで 5%・0.2% → 主Lv1つで 1/unit） */
    statPerLevel: 0.05 / 9,
    agiPerLevel: 0.002 / 9,
    /** 上限（Lv1000）以降：EXP（倍率を除いた素の分）何点で霊玉1つか、1日の上限 */
    overflowExpPerReidama: 400,
    overflowDailyCap: 60,
  },
  /**
   * 兵糧（GAME_DESIGN 20章）：手動とオート周回の1戦で3。4分で1回復（1日360＝120戦。同接の「5分以内の行動」に収まるように）。
   * 掃討は兵糧ではなく掃討チケット（sweep.tickets*）。上限は本体180＋主Lvの段＋兵糧庫、予備900（合わせて3日ぶん以上。2026-10-30 に予備 360 → 900）。BALANCE.md 29章
   */
  stamina: {
    regenMinutes: 4,
    baseMax: 180,
    /** 予備：本体が満タンの間の自然回復を貯める上限（数日空けても溢れないよう、本体と合わせて3日ぶん以上。2026-10-30 に 360 → 900） */
    reserveMax: 900,
    /** 課金「兵糧の予備枠」（永続・reserve_plus）で予備の上限に足す分（900 → 1620＝2日ぶん多く。2026-10-30 に +360 → +720）。兵糧そのものは付与しない */
    paidReserveBonus: 720,
    /** 1戦の兵糧（章によらず3） */
    costMin: 3,
    costMax: 3,
    /** 上の難易度も同じ */
    costPerDifficulty: 0,
    /** 掃討で負けたときに消費する割合（未クリアの掃討） */
    sweepLoseRate: 0.5,
  },
  /**
   * 月パス（500円・買い切りの days 日、自動更新なし）：期間中は兵糧の自然回復が regenSpeed 倍速（1日＝JST の自然回復は無料と同じ dailyRegen まで）、
   * 姿＋10種・チャットのアイコン縁10種が選べる。買うたびにその場でおまもりガチャ10連（券は付けない）
   */
  monthly: {
    regenSpeed: 3,
    dailyRegen: 360,
    /** 1回の購入で延びる日数（自動では更新しない。続けて買うと今の期限から延びる） */
    days: 30,
    /** 残りがこの日数を切ったら買い足せる */
    buyWithinDays: 10,
    /** 期限の何日前から「まもなく終わる」を出すか */
    remindDays: 3,
  },
  rewards: {
    /**
     * 1戦のEXP = 戦場の重み × (expPerHyoro + expPerHyoroPerStage × s)。戦場の重みは以前の消費兵糧（章1で5 → 章10で10、難易度+1/段）。
     * 兵糧が1戦1になった（GAME_DESIGN 20章）あとも、報酬の大きさは以前のまま（stageWeight）
     */
    stageWeight: { min: 5, max: 10, perDifficulty: 1 },
    /** 2回目以降の1戦の EXP・銭・材木・ドロップ率の倍率（兵糧1戦1・掃討チケットで戦闘数が約3倍になった分。BALANCE.md 29章） */
    repeatMult: 0.3,
    /** 撃破ボーナス（killExp*。通常の戦い方で平均+2割）を足したぶん、2→1.65・0.1→0.083 に下げて進行日数を据え置いた（BALANCE.md） */
    /** ペース曲線の作り直し（BALANCE.md 17章）で 1.65→2.0・0.083→0.1（里の道場のEXPを減らした分を戦闘へ。EXP2倍の効きを保つ） */
    expPerHyoro: 2.0,
    expPerHyoroPerStage: 0.1,
    expBossMult: 1.5,
    /** 章ごとの1戦のEXPの倍率（1〜10章）。終盤の章ほど実入りを大きくして一気に攻略が進むように（BALANCE.md 17章） */
    chapterExpMult: [1, 1, 1, 1, 1, 1, 1.3, 1.6, 2, 2.5] as number[],
    /** 章ごとの銭・材木・装備のドロップ率の倍率（1〜10章） */
    chapterLootMult: [1, 1, 1, 1, 1, 1, 1.5, 2.2, 3, 3.3] as number[],
    /** 初回クリアの追加EXP = firstClearExpBase + firstClearExpPerStage × s（EXP2倍・寄進の倍率は firstClearExpMult による） */
    firstClearExpBase: 300,
    firstClearExpPerStage: 10,
    /** 初回クリアで兵糧を返すか（2026-11-04 に false：初回クリアでも兵糧を使う。BALANCE.md 54章） */
    firstClearStaminaRefund: false,
    /** 初回クリアの追加EXPの章ごとの倍率（書いていない章は1）。1日目の朝に兵糧のある限り一気に進みすぎたので序盤を減らす（BALANCE.md 53章） */
    firstClearExpChapterMult: [0.15, 0.2, 0.3, 0.5, 0.75] as number[],
    /**
     * 初回クリアの追加EXPにも EXP の倍率（EXP2倍・寄進）を掛けるか。false＝追加EXPは等倍で、倍率は戦闘ぶんだけ。
     * 2026-11-01 false に（BALANCE.md 42章）：熱心フル課金の100面 13日→20日、無課金は変わらず25日
     */
    firstClearExpMult: false,
    loseRate: 0.3,
    /**
     * 撃破ボーナス：ステージの基本EXP × (1 + min(killExpMax, killExpPerPoint × 撃破ポイント))。初回クリアの追加EXPには掛けない。
     * 撃破ポイントは結果の kills と敵の建物から数える（手番数ではないので、1戦を長引かせても増えない）
     */
    killExpPerPoint: 0.03,
    killExpMax: 0.3,
    killExpPoints: { neutral: 1, rival: 2, boss: 2, building: 1 } as Record<'neutral' | 'rival' | 'boss' | 'building', number>,
    zeniBase: 60,
    zeniPerStage: 10,
    /** 材木（勝ち＋敵の建物を壊した数）。里・図面に対して慢性的に足りなかったので 2・2・0.5 → 3・3・0.75（1.5倍。BALANCE.md 31章） */
    zaimokuBase: 3,
    zaimokuPerBuilding: 3,
    zaimokuPerChapter: 0.75,
    forestChapters: [3, 8] as number[],
    forestMult: 1.5,
    /** 難易度1段ごとの報酬の上乗せ */
    difficultyBonus: 0.5,
    dropChance: 0.7,
    bossDropChance: 1,
    sortieCharWeapon: 0.5,
    slotWeights: { weapon: 40, armor: 30, netsuke: 30 },
    rarityWeights: { n: 70, r: 25, sr: 5 } as Record<string, number>,
    /** 章ボスを倒したときの確定ドロップのレアリティ（通常難易度） */
    bossRarityWeights: { n: 0, r: 70, sr: 30 } as Record<string, number>,
    /** 章ボスを上の難易度（difficulty≥1）で倒したとき。極（ssr）はここでだけ出る */
    bossHardRarityWeights: { n: 0, r: 55, sr: 35, ssr: 10 } as Record<string, number>,
    /** ドロップ2倍（課金の永続権利 drop2x）の倍率。本日初出撃の2倍と掛け算 */
    drop2xMult: 2,
    /** 本日初出撃の何戦がドロップ2倍か */
    firstSortieBattles: 3,
    dexReidama: 5,
    /**
     * 戦闘中の宝箱（自分が開けた分。中身の種類は battle の CFG.chestLoot）。銭＝ステージの基本の銭 × zeniMult、
     * 霊玉＝reidama + reidamaPerChapters 章ごとに+1、装備＝通常のドロップと同じ抽選で1つ。
     * 掃討は sweepChance の確率で1つ開けた扱い（中身は sweepWeights）
     */
    chest: { zeniMult: 0.5, reidama: 2, reidamaPerChapters: 3, sweepChance: 0.7, sweepWeights: { zeni: 35, reidama: 20, equip: 30, omamori: 15 } as Record<string, number> },
    /** 出撃していないキャラの武器を追加で1つ落とす確率（勝ったとき。抽選の回数ぶん）。途中でキャラを変えても武器がそろっているように */
    benchWeaponChance: 0.4,
    /**
     * お守りのドロップ（勝ったとき。抽選の回数ぶん）：chance、番人・章ボスを倒していれば guardian。
     * レア度は rarityWeights（おまもりガチャより低め）。宝箱からも出る（中身 omamori）
     */
    omamoriDrop: { chance: 0.15, guardian: 0.35, rarityWeights: { n: 55, r: 32, sr: 11, ssr: 2 } as Record<string, number> },
    bossFirstReidama: 50,
    /** 星1〜3の初回報酬（章 ch で材木は +perChapter*ch）。式神Lv（霊玉）の導入で ★1 10→15・★3 15→20 に増やした（ガチャと取り合うため。BALANCE.md 16章） */
    star: [
      { reidama: 15, zaimoku: 0 },
      { reidama: 0, zaimoku: 15, zaimokuPerChapter: 5 },
      { reidama: 20, zaimoku: 0 },
    ],
    /** 星の初回報酬の霊玉に掛ける章ごとの倍率（書いていない章は1）。序盤は1日で式神が★3まで進化できてしまったので絞る（BALANCE.md 51章） */
    starReidamaChapterMult: [0.1, 0.15, 0.2, 0.4, 0.6, 0.8] as number[],
    /** 章内の星の合計が n に達したら（章ごと・一度きり）。霊玉30・50 → ガチャ券2・4（BALANCE.md 28章） */
    chapterStarBox: [
      { stars: 10, kuji: 2, zaimoku: 0, omamori: false },
      { stars: 20, kuji: 0, zaimoku: 80, omamori: false },
      { stars: 30, kuji: 4, zaimoku: 0, omamori: true },
    ] as { stars: number; reidama?: number; kuji?: number; zaimoku: number; omamori: boolean }[],
    /** 図鑑 n 体ごとにお守り（一度きり） */
    dexOmamoriEvery: 20,
  },
  stageRanking: {
    /** 最小手番ランキングに載るのは 推奨戦力×これ 以下で勝った記録だけ（推奨を大きく上回って攻めれば手番が少ないのは当然なので） */
    powerRatio: 1.2,
  },
  /** 米問屋（課金「米問屋の通い帳」）：兵糧 hyoro → 掃討チケット tickets（戦える数は同じ） */
  komedoiya: { hyoro: 30, tickets: 10 },
  sweep: {
    /** 未クリアの掃討は 推奨戦力×これ 以上、章ボス以外（2026-11-01 推奨を勝率75%に下げたので 1.3→1.5。どちらも勝率97%くらい） */
    unclearedPowerRatio: 1.5,
    /** 掃討チケット（1枚で1戦。兵糧は使わない）：毎日のログインで ticketsDaily、日替わり任務の全達成で ticketsAllClear。所持は ticketCap まで（超えた分は配らない） */
    ticketsDaily: 70,
    ticketsAllClear: 35,
    ticketCap: 210,
    /** 1回の掃討の上限（戦数） */
    maxCount: 50,
    /** 銭の手数料（2026-10-21 に無しに。20＋3×面 → 0） */
    zeniFeeBase: 0,
    zeniFeePerStage: 0,
  },
  equipment: {
    weapon: { atkBase: 4, atkPerTier: 4 },
    armor: { hpBase: 12, hpPerTier: 14, defBase: 1, defPerTier: 1 },
    netsuke: { agiBase: 1, agiPerTier: 0.8, hpBase: 5, hpPerTier: 5 },
    rarityMult: { n: 1, r: 1.15, sr: 1.3, ssr: 1.45 } as Record<string, number>,
    /** 系統の固有効果（%）の倍率（2026-10-20 に 1 → 2.5。系統を選ぶ理由になるよう銘より大きく） */
    traitMult: 2.5,
    /** 相性の術を持っていくと、武器の固有効果がこの倍率 */
    synergyMult: 1.5,
    /** レアリティごとの追加効果の個数 [最小, 最大]（一様） */
    affixCount: { n: [0, 1], r: [1, 2], sr: [2, 3], ssr: [3, 4] } as Record<string, [number, number]>,
    /** 1段ごとの基礎値の伸び（+100で2倍。旧目盛りは1段5%・+20まで） */
    enhancePerLevel: 0.01,
    /** 強化の上限 = levelCapBase + levelCapPerLb * 合成（限界突破）。+10ごとに合成（同じ装備でレア度が同じか上のものを1つ素材に） */
    levelCapBase: 10,
    levelCapPerLb: 10,
    lbMax: 9,
    /** +1→ +n の費用 = enhanceBase * n^enhanceExp * (tierBase + tierPer*tier)（+100までの合計は旧の+20までの約3倍） */
    enhanceBase: 25,
    enhanceExp: 1.25,
    enhanceTierBase: 0.8,
    enhanceTierPer: 0.2,
    limitBreakZeni: 500,
    dismantleBase: 30,
    inventoryBase: 150,
    inventoryPerSlotPack: 50,
    /** 所持枠+50 を買える上限（枠の合計） */
    inventoryMax: 500,
    /** 追加効果の値 = (lo + (hi-lo)*q) * (meiTierScaleBase + meiTierScalePer * 段) * affixValueMult */
    meiTierScaleBase: 0.7,
    meiTierScalePer: 0.03,
    /** 追加効果の値の全体倍率（economy-sim の進行日数で合わせる） */
    affixValueMult: 0.4,
    /** 追加効果の抽選の重み（書いていないものは1）。陣材収入・復活は極だけに出る（AFFIX_POOL.minRarity） */
    affixWeights: { matIncome: 0.5, respawn: 0.5 } as Record<string, number>,
    /** 極のドロップが名物になる確率（その段までに出る同じ部位の名物があるとき） */
    meibutsuChance: 0.25,
    /** ハード以上（difficulty>=1）：良以上のドロップが業物（セット）になる確率・極が神品になる確率と神品の銘の個数 */
    setChance: 0.25,
    shinChance: 0.3,
    shinAffixCount: [4, 5] as [number, number],
    /** デメリット付きの銘が出るのはハード以上。出る重み（ほかの銘は1） */
    drawbackWeight: 0.6,
  },
  /**
   * 万屋（里から。銭で買う。BALANCE.md 32章）。品物は n 回目の値段 = price ×(1+rise)^n（100銭単位）、日付が変わると戻る。daily は1日の上限（0 なら無し）。
   * 材木は銭の余り（里の費用の比 銭28：材木1 に対して入りは 銭92：材木1）を材木に回す受け皿
   */
  shop: {
    goods: {
      zaimoku: { name: '材木', amount: 100, price: 3000, rise: 0.2, daily: 0 },
      kuji: { name: 'ガチャ券', amount: 1, price: 3000, rise: 0.5, daily: 5 },
      fuse: { name: '錬成札', amount: 1, price: 5000, rise: 0.5, daily: 3 },
    } as Record<'zaimoku' | 'kuji' | 'fuse', { name: string; amount: number; price: number; rise: number; daily: number }>,
    /** 道具の解放の値段（一度買えばずっと。主Lv が ITEM_UNLOCK に届くと並ぶ。回復薬は最初から） */
    items: { heal: 0, power: 3000, guard: 10000, swift: 25000, purify: 50000, spirit: 80000 } as Record<string, number>,
  },
  /**
   * 戦力の式（loadout.ts の powerFactors）で、戦闘のフラグ（スキル・お守りの特殊効果）に付ける重み。flag 倍率 = 1 + 重みの合計。
   * 実戦の効き目に合わせる（battle-sim --meta-power で推奨1.0倍の勝率がそろうように。BALANCE.md 10章）
   */
  /**
   * 戦い方が変わる効果（戦闘のフラグ）が装備・銘・業物・スキル・式神から重なったとき、2つ目以降1つにつき足す数値（2026-10-22）。
   * 戦闘はフラグを「持っているか」だけで見るので、重ねても強くならず無駄になっていた
   */
  dupFlag: {
    regen: [{ key: 'hp', value: 0.05 }],
    sanctuary: [{ key: 'taken', value: -0.04 }],
    siege: [{ key: 'core', value: 0.12 }],
    predator: [{ key: 'neutral', value: 0.15 }],
    conqueror: [{ key: 'territory', value: 0.03 }],
    counter: [{ key: 'atk', value: 0.04 }],
    bu_counter: [{ key: 'atk', value: 0.04 }],
    kaeshi: [{ key: 'atk', value: 0.04 }],
    riposte: [{ key: 'atk', value: 0.04 }],
    duelist: [{ key: 'atk', value: 0.04 }],
    execution: [{ key: 'atk', value: 0.04 }],
    momentum: [{ key: 'atk', value: 0.04 }],
  } as Record<string, { key: ModKey; value: number }[]>,
  power: {
    /**
     * 戦力を能力値（HP・攻撃・防御・速さ）だけで出す。false で効果の見積もり（術の威力・魔法・射程・建物・賽・スキルの効果）も掛ける。
     * 2026-11-01 false に：能力値だけだと術・魔法・射程が主力のアカリが低く出て、同じ戦力でのキャラ差も大きかった（BALANCE.md 45章）
     */
    statOnly: false,
    /** battle-sim の較正（tools/sim/flag-cal.ts：31〜89面、推奨0.9倍で付けた／外した勝率の差を戦力に換算）で決めた値 */
    flags: {
      /** 風車守（手番の初めに50%で移動+1）：移動+1 は戦力の char 因子で+5% なので、その半分 */
      'omamori:kazaguruma': 0.025,
      predator: 0.02,
      conqueror: 0.025,
      momentum: 0.065,
      riposte: 0.025,
      execution: 0.005,
      regen: 0.02,
      sanctuary: 0.006,
      siege: 0.033,
      bu_counter: 0.01,
      counter: 0.01,
      'ougi:akari_lantern': 0.065,
      'ougi:kazamaru_senpu': 0.01,
      'ougi:yuzuha_kekkai': 0.014,
      // 型のスキル（BALANCE.md 23章）。初めの見積もり：stride は勢いと同じ、ほかは試作の勝率差から
      stride: 0.065,
      hitrun: 0.04,
      kaeshi: 0.03,
      duelist: 0.04,
      fushin_guard: 0.01,
      fushin_repair: 0.005,
      fushin_endure: 0.005,
      fushin_lead: 0.01,
      // 装備の系統の戦い方（2026-10-20 flag-cal：31〜89面 RUNS=16。atk10 との比で ×0.7）
      eq_ignite: 0.011,
      eq_bind: 0.006,
      eq_stagger: 0.018,
      eq_cleave: 0.007,
      eq_double: 0.012,
      eq_paint: 0.035,
      eq_knock: 0.01,
      eq_volley: 0.028,
    } as Record<string, number>,
    /** surge:fast:-1 の1点あたり */
    surgeFast: 0.025,
    /** 賽の条件の効果（丁・大目・揃い目・役・半）の見積もりの倍率（battle-sim の較正。1 だと博徒の振り方の戦力が勝率より2割ほど高く出た） */
    diceScale: 0.45,
    /** surge:boost:<キャラ>:<段> の重み（倍率1.5でこの値、2.0で2倍） */
    surgeBoost: {
      akari: [0, 0, 0.008, 0.015],
      kazamaru: [0, 0, 0.005, 0.007],
      suzune: [0, 0, 0.035, 0],
      yuzuha: [0, 0, 0, 0.016],
    } as Record<string, number[]>,
    /** surge:on<段>:<効果> */
    surgeOn: { heal: 0.002, mat: 0.003, skill: 0.006 } as Record<string, number>,
    /** お供の能力（フラグ comp:<キー>=<値>）の重み：rules に 1 + 重み × 値。数値で渡す能力（陣材・建物HP・薬など）は他と同じ式で入る */
    companion: {
      mizube: 0.4,
      kasa: 0.023,
      kuda: 0.02,
      dash: 0.009,
      trap: 0.09,
      relay: 0.06,
      hangon: 0.018,
      surge: 0.03,
      slow: 0.0075,
      rise: 0.13,
      cd: 0.021,
      mirror: 0.03,
      burn: 0.12,
      feast: 0.15,
    } as Record<string, number>,
    /**
     * 式神の奥義（フラグ shiki:<条件>:<効果>=<値>）の重み：rules に Σ(効果の重み × 値) × 条件の倍率。power は (値-1) に掛ける。
     * 1戦に1回なので小さめ（BALANCE.md 16章）
     */
    ougiEffect: { heal: 0.06, guard: 0.012, power: 0.1, skill: 0.015, mat: 0.0008, paint: 0.006, surge: 0.03, bind: 0.01, seal: 0.008, burn: 0.08, blast: 0.04, quake: 0.25, might: 0.012, ward: 0.012, move: 0.006, build: 0.008, repair: 0.02 } as Record<string, number>,
    ougiTrigger: { start: 1, mid: 0.85, pinch: 0.8, surge3: 0.6, boss: 0.5, core: 0.75, kill: 0.6, revive: 0.5, turn3: 0.9, skill: 0.8, build: 0.6 } as Record<string, number>,
    /** 式神の定期の加護（boon:）の重み：値 × 最大の重ね × これ（種類ごと） */
    boon: { atk: 0.25, def: 0.2, regen: 1.2 } as Record<string, number>,
    /**
     * 数値で渡すお供の能力の、式の見積もりと実戦の差の補正：rules に（能力の値 × これ）を足す（効果の種類ごと）。
     * 旧 tools/sim/comp-cal.ts（削除済み）で測った値（BALANCE.md「式神とお供」）。AI同士の実戦では陣材・建物HP・ボス与ダメの効き目が小さい
     */
    companionAdj: {
      matStart: -0.001,
      matIncome: -0.015,
      buildHp: -0.027,
      buildPower: 0.04,
      potionCount: -0.012,
      boss: -0.1,
      core: 0,
      neutral: -0.1,
      taken: -0.18,
      agi: -0.1,
      lifesteal: -0.1,
      splash: 0.08,
      territory: -0.75,
    } as Record<string, number>,
    /**
     * 式神の持っているだけの効果（上限後）の、式の見積もりと実戦の差の補正：rules に（効果の値 × これ）を足す。
     * battle-sim --meta-power で想定の式神の★を変えて測った（防御・中立与ダメは式の見積もりより実戦で効く）
     */
    shikigamiAdj: { def: 0.25, neutral: 0.3 } as Record<string, number>,
    /**
     * 射程（1を超えた分1につき）・魔法（防御を半分無視）・移動（1につき）の戦力の倍率。2026-11-01 推奨1.0倍のキャラ別勝率がそろうように
     * char-sim で合わせた（移動 0.05→0.15・魔法 0.08→0.04。キャラごとの係数 charMult はやめた）
     */
    kit: { range: 0.06, magic: 0.04, move: 0.15 },
    /** 建築（建てられる数・建物の強さ・陣材など）の見積もり全体に掛ける（同じ較正で 0.5。建築が主力のスズネが高く出ていた） */
    buildScale: 0.5,
    /** 吸収（与ダメの回復）の重み：rules に 1 + これ × lifesteal */
    lifesteal: 0.15,
  },
  /** スキル（skills.ts）。解放力 = base + 主Lv × perLevel ＋上乗せ（合計 bonusMax まで） */
  skills: {
    capacity: {
      base: 5,
      /** 旧Lv1つ（主Lv unit=9 個）ごとに+1（levelTier） */
      perLevel: 1,
      /** 式神 shikiPer 体ごとに+1（最大 shikiMax） */
      shikiPer: 1.6,
      shikiMax: 6,
      /** 里の道場 dojoPer Lvごとに+1（最大 dojoMax） */
      dojoPer: 4,
      dojoMax: 5,
      /** 外伝1つクリアごとに */
      gaidenEach: 1,
      gaidenMax: 4,
      bonusMax: 15,
    },
    /** スキルの%効果に掛ける倍率（較正用。段階的な効果＝移動・待ち手番・上限などには掛けない） */
    effectMult: 1,
    /** 系統（skill-lines）の Lv 1つあたりの能力の倍率（系統の per × Lv × これ） */
    linePerLv: 1.6,
    /** 系統のキャラの、主Lvで上がる基本の能力の割合（残りは系統の Lv の能力で） */
    lineGrow: 0.6,
    /** 構成の保存枠：無料の枠数と、権利 build_slots で使える枠数 */
    freePresetSlots: 1,
    presetSlots: 10,
    /** おすすめ構成で、キャラの系統に合う共通スキルの優先度に足す値 */
    autoLean: {
      akari: { tactics: 0 },
      kazamaru: { body: 0 },
      suzune: { build: 0 },
      yuzuha: { body: 0 },
    } as Record<string, Partial<Record<'body' | 'tactics' | 'build', number>>>,
    /** 旧スキルツリーのリセット券の補填：1枚目は保存枠（build_slots）、残りは1枚ごとに銭 */
    legacyResetZeni: 3000,
  },
  shikigami: {
    /**
     * 1体倒したときの勧誘率（GAME_DESIGN 16章）。仲間になるのはエリート（各章2体）と章ボス（各章1体）の30体だけ。
     * 天井（確定までの撃破数）は無い。章ボスはレアで強い枠（★上げも同じ率）で、初回撃破だけ確定。小型・中型は図鑑と固有装備のドロップだけ
     */
    baseRate: { elite: 0.02, boss: 0.01 } as Record<string, number>,
    /** 章ボスを初めて倒したとき必ず仲間にする（まだ仲間でなければ、勝ち負けによらず撃破で確定） */
    bossFirstGuarantee: true,
    fudaMult: 2,
    /** 式神の社：Lv1ごとに勧誘率に足す（1体あたり。2026-11-03 に掛け算の+5%から足し算の+1%へ） */
    shrinePerLevel: 0.01,
    /** 使役率3倍（課金の永続権利 recruit3x）。札・式神の社と掛け算 */
    recruit3xMult: 3,
    maxStar: 5,
    /**
     * 式神Lv（GAME_DESIGN 16.3）：霊玉で上げる。上限 = ★ × levelPerStar（★1:Lv10 … ★5:Lv50）。
     * Lv L→L+1 の霊玉 = round(base + (top - base) × ((L-1)/(maxLevel-2))^exp)（Lv1→2 が5、Lv49→50 が60）。
     * 霊玉がいつも0で式神が育たなかったので top 100 → 60（1体を Lv50 にするのに 2,364 → 1,518。BALANCE.md 31章）
     */
    maxLevel: 40,
    levelPerStar: 10,
    levelCost: { base: 5, top: 60, exp: 1.2 },
    /**
     * 持っているだけの効果：役割ごとの [Lv1, Lv50] の値（間は Lv の直線）。
     * 章ボスは育ち切ると1体で推奨の1.4〜1.55倍に当たり運の差が大きかったので、章ボスの伸び幅（Lv1→Lv50 の増分）を 0.6 倍にした
     * （hold 25%→17%・passive 10%→7%、companion・second・ougi の章ボス10体の Lv50 の値も同じ。BALANCE.md 17章）
     */
    hold: { elite: [0.02, 0.1], boss: [0.05, 0.17], shiki: [0.03, 0.15] } as Record<string, [number, number]>,
    /** ★2 の常時効果：役割ごとの [Lv1, Lv50] の値 × 効果の種類の倍率（passiveMult、書いていないものは1） */
    passive: { elite: [0.01, 0.05], boss: [0.02, 0.07], shiki: [0.015, 0.06] } as Record<string, [number, number]>,
    passiveMult: { buildHp: 3, buildPower: 2, boss: 2, skillPower: 1.5, potionHeal: 2, agi: 1, taken: 0.5 } as Record<string, number>, // agi 0.5 → 1（崖なしの CTB で速さは攻撃と同じ効き目。BALANCE.md 38章）
    /**
     * 効果ごとの合計の上限（持っているだけの効果＋★2の常時効果）。以前の 15%/10% は「育てても伸びない」原因だったので引き上げた（BALANCE.md 16章）。
     * 建物HPは章ボスの伸び幅を抑えた（17章）ので 0.7→0.6（全員★5・Lv50 で上限に届く値）。書いていない効果は 0.1
     */
    cap: {
      hp: 0.4,
      atk: 0.4,
      def: 0.4,
      core: 0.35,
      neutral: 0.35,
      buildHp: 0.6,
      agi: 0.2, // 0.1 → 0.2（速さの式神は木の葉天狗・管狐の2体で合計 0.15 ほど。BALANCE.md 38章）
      skillPower: 0.25,
      buildPower: 0.4,
      boss: 0.3,
      taken: 0.08,
      potionHeal: 0.2,
    } as Record<string, number>,
    /**
     * お供（連れていく式神の能力）を使うか。false＝コレクション＋能力値だけ（お供の枠・能力なし。相性のキャラに大きく効く：affinity）
     */
    useCompanion: false,
    /**
     * お供を使わないとき（コレクション＋能力値。4人に同じだけ効く）：持っているだけの効果 × hold（★3 で ×star3、★5 で ×star5）、
     * ★2 の常時効果 × passive（★4 から ×passive4）。お供の分を埋める倍率（battle-sim：想定育成の勝率をお供ありとそろえる）
     */
    // 式神の作り直し（加勢あり）：持っている効果は半分に（存在感は加勢が担う。想定育成で通常84%・章ボス59%、作り直し前は75%・45%）
    collection: { hold: 0.5, star3: 1.2, star5: 1.2, passive: 1, passive4: 1.5 },
    /**
     * 式神（10種族×★1〜★3の進化）：素体（バトルに出る小型・中型）を倒すと勧誘。仲間になったら式神Lv（霊玉）で育て、
     * evolveAt の Lv で★2・★3 に進化（見た目と加勢の威力・回数が上がる）。仲間の種族はもう勧誘しない
     */
    // firstRate：素体を1体倒したときの勧誘率（ふつう1%。社で+1%ずつ、札×2・勧誘率3倍×3は合計に掛ける）。2026-11-03 に 8% → 1%
    // speciesMult：1戦で倒せる数が少ない種族の倍率（よく出る戦場で1戦に1体ほど。2〜4体倒せる種族と体感を近づける。画面には出さない）
    evo: { evolveAt: [15, 30] as [number, number], maxLevel: 40, baseRate: 0.005, firstRate: 0.01, speciesMult: { e001: 3, e041: 2, e067: 4, e071: 2 } as Record<string, number> },
    /** ★4：1つ目のお供能力の上位版。整数の部品（回数・手番・マス・陣材）は +upgradeInt、それ以外は ×upgradeMult */
    upgradeInt: 1,
    upgradeMult: 1.25,
    /** ★最大の式神を倒したときの銭（役割ごと、1体ごと）。勧誘の抽選はしない。使役率3倍・札で増えない */
    maxedKillZeni: { elite: 200, boss: 400 } as Record<string, number>,
    /** 旧データの移行：式神でなくなった小型・中型の★1つあたりの銭（霊玉は付けない） */
    legacyStarZeni: 300,
    /** 旧データの移行：式神Lv の導入前に★を上げていた分（★-1 つごと）の霊玉（課金の経路ではないので霊玉で補填） */
    legacyStarReidama: 30,
    /**
     * お供の能力の値（★1から）。式神ごとの能力（shikigami.ts の COMPANIONS）→ 部品 → [Lv1 の値, Lv50 の値]（間は Lv の直線）。
     * 章ボス10体の Lv50 の値は伸び幅を 0.6 倍にしてある（整数の部品は増分を四捨五入。BALANCE.md 17章）。
     * 整数の部品（回数・陣材など）は切り捨て。説明は shikigami.ts が作る。★4 で上位版（upgradeInt・upgradeMult）
     */
    companion: {
      hitotsume: { trap: [0.15, 0.6] }, // 一つ目小僧：罠を見破る＋相手の建物への与ダメ
      bakegitsune: { kuda: [1, 3] }, // 化け狐：中立を倒すと妖力+1（回数）
      odanuki: { matStart: [5, 11], matIncome: [1, 2], hp: [0.07, 0.21] }, // 大たぬきの霊：開始時の陣材＋毎手番の陣材＋HP
      akaei: { buildHp: [0.4, 1.8] }, // 赤えい：建物HP
      umibozu: { mizube: [0.04, 0.12] }, // 海坊主：水辺で回復
      ushioni: { hp: [0.06, 0.19], atk: [0.04, 0.12] }, // 牛鬼：HP・攻撃
      okuriookami: { neutral: [0.15, 0.65] }, // 送り狼：中立への与ダメ
      takekiri: { matStart: [3, 10] }, // 竹切り狸：開始時の陣材
      kyubi: { surge: [1, 2], kuda: [1, 2], skillPower: [0.05, 0.15] }, // 九尾の影：最初の手番に妖力＋中立撃破で妖力
      ungaikyo: { boss: [0.4, 1.6] }, // 雲外鏡：ボスへの与ダメ
      honegasa: { kasa: [1, 3] }, // 骨傘：大きな攻撃を防ぐ（回数）
      gashadokuro: { hangon: [1, 2], hp: [0.07, 0.21] }, // がしゃどくろ：早く復活（回数）＋HP
      tsurara: { buildPower: [0.15, 0.65] }, // つらら童：建物の効果
      yukiitachi: { dash: [2, 5] }, // 雪鼬：最初の手番の移動+1
      yukionna: { slow: [2, 3], def: [0.08, 0.24], skillPower: [0.08, 0.24] }, // 雪女：近くの相手ヒーローの移動-1（マス）＋防御＋術
      hinezumi: { taken: [0.03, 0.12] }, // 炎鼠：被ダメ軽減
      onmoraki: { lifesteal: [0.05, 0.2] }, // 陰摩羅鬼：吸収
      wanyudo: { dash: [2, 4], core: [0.08, 0.24], atk: [0.03, 0.09] }, // 輪入道：最初の手番の移動+1＋本陣への与ダメ
      ubume: { potionCount: [1, 2], potionHeal: [0.04, 0.2] }, // 産女：回復薬+1・回復薬の回復
      kageonna: { hangon: [1, 2] }, // 影女：早く復活（回数）
      onryo: { cd: [1, 2], skillPower: [0.1, 0.28], atk: [0.03, 0.09] }, // 怨霊将軍：術の待ち-1（回数）＋術の威力
      amanojaku: { mirror: [0.3, 0.9] }, // 天邪鬼：隣からの攻撃に反撃（攻撃の割合）
      karasu: { relay: [0.3, 0.8] }, // 鴉兵：龍脈を占領すると回復
      daitengu: { splash: [0.3, 0.72], dash: [2, 4], agi: [0.04, 0.11] }, // 大天狗：術の範囲＋最初の手番の移動+1
      kijo: { rise: [0.2, 0.6] }, // 鬼女：一度だけ倒れずに立ち上がる（HPの割合）
      onibiban: { surge: [1, 3] }, // 鬼火番：最初の手番に妖力ポイント
      ibaraki: { core: [0.12, 0.35], boss: [0.3, 0.84], atk: [0.03, 0.09] }, // 茨木童子：本陣への与ダメ＋ボスへの与ダメ
      amikiri: { territory: [0.02, 0.08] }, // 網剪：陣地率
      tenome: { agi: [0.04, 0.16] }, // 手の目：速さ
      shuten: { kasa: [1, 3], atk: [0.04, 0.12] }, // 酒呑童子：大きな攻撃を防ぐ（回数）＋攻撃
    } as Record<string, Record<string, [number, number]>>,
    /**
     * ★3 の第二のお供能力（式神ごとに固有。敵ID → 部品 → [Lv1, Lv50]）。部品はお供能力と同じ（comp:*・数値）と、
     * 既存の技のフラグ（siege・execution・momentum・sanctuary・regen・conqueror・riposte・predator・bu_counter。値は1＝有り）
     */
    second: {
      e008: { slow: [1, 2], taken: [0.02, 0.06] },
      e009: { burn: [0.1, 0.35] },
      e010: { surge: [1, 2], def: [0.05, 0.14], matIncome: [1, 2] },
      e018: { mizube: [0.02, 0.06], buildPower: [0.08, 0.3] },
      e019: { splash: [0.15, 0.5], hp: [0.03, 0.12] },
      r02: { siege: [1, 1], atk: [0.03, 0.08], hp: [0.03, 0.08] },
      e028: { execution: [1, 1], neutral: [0.08, 0.3] },
      e029: { buildHp: [0.2, 0.8], matIncome: [1, 2] },
      e030: { kasa: [1, 2], skillPower: [0.05, 0.14], surge: [1, 2] },
      e038: { mirror: [0.2, 0.6], boss: [0.1, 0.4] },
      e039: { dash: [1, 3], agi: [0.02, 0.06] },
      e040: { rise: [0.15, 0.33], hp: [0.04, 0.11], def: [0.03, 0.07] },
      e048: { splash: [0.15, 0.5], atk: [0.02, 0.08] },
      e049: { momentum: [1, 1], agi: [0.02, 0.06] },
      r05: { sanctuary: [1, 1], def: [0.04, 0.11], taken: [0.02, 0.04] },
      e058: { burn: [0.1, 0.35], core: [0.04, 0.15] },
      e059: { feast: [0.08, 0.25] },
      r06: { burn: [0.12, 0.29], lifesteal: [0.03, 0.07], core: [0.04, 0.11] },
      e068: { regen: [1, 1], hp: [0.03, 0.1] },
      e069: { slow: [1, 2], def: [0.03, 0.1] },
      r07: { conqueror: [1, 1], kuda: [1, 2], atk: [0.03, 0.07] },
      e078: { riposte: [1, 1], taken: [0.02, 0.06] },
      e079: { predator: [1, 1], agi: [0.02, 0.06] },
      e080: { cd: [1, 2], skillPower: [0.06, 0.17], surge: [1, 2] },
      e088: { feast: [0.06, 0.2], atk: [0.03, 0.1] },
      e089: { burn: [0.08, 0.3], buildPower: [0.08, 0.3] },
      r09: { bu_counter: [1, 1], def: [0.05, 0.13], boss: [0.1, 0.28] },
      e098: { trap: [0.1, 0.4], matIncome: [1, 2] },
      e099: { kasa: [1, 2], agi: [0.02, 0.06] },
      r10: { regen: [1, 1], lifesteal: [0.04, 0.09], atk: [0.04, 0.11], hp: [0.04, 0.11] },
    } as Record<string, Record<string, [number, number]>>,
    /**
     * ★5 の奥義（式神ごとに固有。1戦に1回、条件 trigger で効果 effects がまとめて出る。値は [Lv1, Lv50]）。
     * 条件・効果の意味は battle/index.ts の BATTLE_FLAGS（shiki:<条件>:<効果>=<値>）
     */
    ougi: {
      e008: { trigger: 'pinch', effects: { bind: [1, 2], seal: [1, 1] } },
      e009: { trigger: 'surge3', effects: { paint: [1, 2], heal: [0.15, 0.35] } },
      e010: { trigger: 'start', effects: { paint: [1, 2], mat: [5, 9], guard: [1, 1] } },
      e018: { trigger: 'pinch', effects: { guard: [1, 2], heal: [0.1, 0.25] } },
      e019: { trigger: 'mid', effects: { blast: [0.6, 1.5] } },
      r02: { trigger: 'core', effects: { quake: [0.04, 0.08], power: [1.2, 1.44] } },
      e028: { trigger: 'kill', effects: { power: [1.3, 1.8], heal: [0.1, 0.25] } },
      e029: { trigger: 'start', effects: { mat: [4, 10], guard: [1, 1] } },
      e030: { trigger: 'surge3', effects: { blast: [0.8, 1.52], skill: [1, 1], heal: [0.1, 0.22] } },
      e038: { trigger: 'boss', effects: { power: [1.4, 2.2], seal: [1, 1] } },
      e039: { trigger: 'pinch', effects: { guard: [1, 2], power: [1.2, 1.5] } },
      e040: { trigger: 'revive', effects: { blast: [1.0, 1.72], guard: [1, 2] } },
      e048: { trigger: 'mid', effects: { blast: [0.5, 1.2], bind: [1, 1] } },
      e049: { trigger: 'start', effects: { power: [1.3, 1.8], surge: [1, 1] } },
      r05: { trigger: 'pinch', effects: { bind: [1, 2], seal: [1, 2], heal: [0.1, 0.22] } },
      e058: { trigger: 'mid', effects: { blast: [0.5, 1.2], burn: [0.06, 0.15] } },
      e059: { trigger: 'kill', effects: { heal: [0.25, 0.6], surge: [1, 2] } },
      r06: { trigger: 'core', effects: { quake: [0.05, 0.09], burn: [0.08, 0.15], power: [1.2, 1.44] } },
      e068: { trigger: 'pinch', effects: { heal: [0.25, 0.6] } },
      e069: { trigger: 'revive', effects: { power: [1.3, 1.8], skill: [1, 1] } },
      r07: { trigger: 'surge3', effects: { blast: [0.8, 1.4], mat: [5, 8], power: [1.2, 1.38] } },
      e078: { trigger: 'pinch', effects: { power: [1.5, 2.2] } },
      e079: { trigger: 'mid', effects: { paint: [1, 2], surge: [1, 2] } },
      e080: { trigger: 'surge3', effects: { blast: [1.0, 1.72], bind: [1, 2] } },
      e088: { trigger: 'pinch', effects: { power: [1.4, 2.0], heal: [0.1, 0.3] } },
      e089: { trigger: 'boss', effects: { blast: [0.5, 1.2], burn: [0.06, 0.15] } },
      r09: { trigger: 'boss', effects: { power: [1.6, 2.14], quake: [0.03, 0.06] } },
      e098: { trigger: 'core', effects: { paint: [1, 2], quake: [0.03, 0.08] } },
      e099: { trigger: 'kill', effects: { skill: [1, 1], power: [1.3, 1.7], heal: [0.1, 0.2] } },
      r10: { trigger: 'surge3', effects: { heal: [0.3, 0.48], power: [1.4, 1.64], guard: [1, 2], blast: [1.0, 1.6] } },
    } as Record<string, { trigger: string; effects: Record<string, [number, number]> }>,
  },
  blueprint: {
    maxLevel: 20,
    zaimokuBase: 35,
    zaimokuExp: 1.5,
    zeniBase: 200,
    zeniExp: 1.4,
  },
  village: {
    capHours: 24,
    upgradeZeniBase: 500,
    upgradeZeniExp: 1.6,
    upgradeZaimokuBase: 30,
    upgradeZaimokuExp: 1.4,
    maxLevel: 20,
    /** 施設Lvの上限 = levelBase + levelPerProvince × 制圧した地方の数（20地方中18地方で上限20。互換のため設定名は維持） */
    levelBase: 2,
    levelPerProvince: 1,
    kuraZeniPerHour: 60,
    /** 祠Lv1つで1時間あたりの霊玉。式神Lv が育たないので 0.05 → 0.25（5倍。祠Lv10で1日60個。BALANCE.md 27章） */
    hokoraReidamaPerHour: 0.25,
    /** 製材所Lv1つで1時間あたりの材木。里・図面に材木が追いつかなかったので 3 → 6（2倍。BALANCE.md 31章） */
    seizaiZaimokuPerHour: 6,
    /** ペース曲線の作り直し（BALANCE.md 17章）で 35→18（EXP2倍が掛からない分を減らし、戦闘のEXPへ） */
    dojoExpPerHour: 18,
    hyorograMaxPerLevel: 5,
    hyorograReservePerLevel: 10,
    /** 鍛冶屋 Lv がこれだけ上がるごとに装備の合成（強化の上限+10）が1回できる */
    smithLevelsPerLb: 2,
  },
  /**
   * おまもりガチャ（GAME_DESIGN 18章・19章）。1回ごとに抽選ランク（S〜D。キーは互換のため daikichi〜kyo）を引き、景品を引く。お守りのレア度はランクで変わる。
   * ガチャ券で引く（券は遊んで手に入る分だけ。課金の経路なし）。無料の1回は JST の日付で1日1回（券不要）
   */
  gacha: {
    /** ガチャ券の枚数（1回1枚・10連10枚）。霊玉では引かない（霊玉は式神Lv だけ。BALANCE.md 28章） */
    cost1: 1,
    cost10: 10,
    /** 天井：極のお守りが出ないまま何回（札・無料の合計）で極が確定か */
    pity: 80,
    /** 景品の重み。2026-11-02 からお守りだけ（EXP でLvが上がりすぎる・名前どおりに。銭は並・良を売って戻る）。お守り以外は ランクS なら量が2倍 */
    rates: { omamori: 100, fuda: 0, zaimoku: 0, zeni: 0, exp: 0, equipment: 0, meibutsu: 0 } as Record<string, number>,
    /** お守りに添える銭（1回ごと。ランクS は daikichiAmountMult 倍）。0 なら付けない（2026-11-02 に 300 を試して外した） */
    omamoriZeni: 0,
    expAmount: 300,
    zeniAmount: 2000,
    zaimokuAmount: 40,
    /** 装備の景品のレアリティ（名物は別枠で極の名物） */
    equipRarity: { r: 70, sr: 30 },
    /** ランクの重み（S・A・B・C・D） */
    fortuneWeights: { daikichi: 5, chukichi: 15, kichi: 35, suekichi: 30, kyo: 15 } as Record<string, number>,
    /** ランクごとのお守りのレア度の重み（並・良・優・極）。全体で 極 約1.5%・優 約11%・良 約35%・並 約52%（毎回お守りになったので以前より絞る） */
    rarityByFortune: {
      daikichi: { n: 0, r: 35, sr: 50, ssr: 15 },
      chukichi: { n: 15, r: 50, sr: 31, ssr: 4 },
      kichi: { n: 50, r: 40, sr: 9.5, ssr: 0.5 },
      suekichi: { n: 65, r: 32, sr: 3, ssr: 0 },
      kyo: { n: 85, r: 15, sr: 0, ssr: 0 },
    } as Record<string, Record<string, number>>,
    /** ランクS の景品（お守り以外）の量の倍率 */
    daikichiAmountMult: 2,
    /** 10連の確定：ランクS・A が1つも無ければ最後の1回を S・A（S:A の重みはそのまま）で引き直す */
    tenGuarantee: ['daikichi', 'chukichi'] as string[],
    /** 週替わりのピックアップ（JST の週で自動）：その主効果の重みの倍率（他は1） */
    pickupMult: 3,
    /** ランクD を引いた翌日の無料ガチャ：S・A の重みの倍率 */
    kyoNextDayMult: 3,
    /** 無料の1回のランクで付く、その日だけの小さなボーナス（値は倍率の上乗せ）。A はその日のガチャで S・A の重みが luck 倍、D は翌日の無料で S・A が出やすい。B・C はボーナス無し（進行日数を変えないため。BALANCE.md 15章） */
    perks: {
      daikichi: { drop: 0.2 },
      chukichi: { luck: 1.5 },
      kichi: {},
      suekichi: {},
      kyo: {},
    } as Record<string, Partial<Record<'drop' | 'zeni' | 'zaimoku' | 'luck', number>>>,
  },
  /** お守りのランダム生成・錬成・売却・交換所（GAME_DESIGN 18章） */
  omamori: {
    /** レア度ごと：主効果の品質 q の幅・副効果の個数 [最小, 最大]・副効果の Lv の上限 */
    rarity: {
      n: { q: [0, 0.6], subs: [0, 1], lvMax: 2 },
      r: { q: [0.15, 0.75], subs: [1, 2], lvMax: 3 },
      sr: { q: [0.3, 0.9], subs: [2, 3], lvMax: 4 },
      ssr: { q: [0.5, 1], subs: [3, 3], lvMax: 5 },
    } as Record<string, { q: [number, number]; subs: [number, number]; lvMax: number }>,
    /** 副効果の Lv の重み（Lv1..5。上限より上は除く） */
    lvWeights: [40, 30, 18, 9, 3],
    /**
     * 副効果：per = Lv1つあたりの値、cap = 効果ごとの上限（per×6＝Lv5 の値より1 Lv 分上。枠が1つ・副効果は重複なしなので1つでは当たらない安全柵。
     * 2026-10-21 の1枠化の前は装備中3つの合計の上限で per×6〜7.5）、w = 抽選の重み。
     * surge（妖力の溜まりやすさ）は Lv なし（最初の手番に妖力の撃破ポイント+1）で、優・極だけ・装備全体で1つ分
     */
    subs: {
      atk: { per: 0.008, cap: 0.048, w: 10 },
      hp: { per: 0.012, cap: 0.072, w: 10 },
      def: { per: 0.012, cap: 0.072, w: 10 },
      agi: { per: 0.008, cap: 0.048, w: 8 }, // 速さは戦力で攻撃と同じ重み（崖なしの CTB で実戦も同じ）なので攻撃と同じ値に（BALANCE.md 38章）
      skillPower: { per: 0.015, cap: 0.09, w: 8 },
      buildHp: { per: 0.03, cap: 0.18, w: 8 },
      buildPower: { per: 0.02, cap: 0.12, w: 6 },
      core: { per: 0.015, cap: 0.09, w: 8 },
      neutral: { per: 0.02, cap: 0.12, w: 8 },
      surge: { per: 1, cap: 1, w: 2 },
    } as Record<string, { per: number; cap: number; w: number }>,
    /**
     * 主効果の幅：q=1 が旧お守りの段 1+4×mainSpan（0.35 で段2.4）。副効果の値・上限の全体倍率 subMult。
     * economy-sim で「お守りによる戦力の倍率」と100面の日数が旧お守り（段の重ね）とほぼ同じになるように合わせた（BALANCE.md 15章）。
     * 枠を3 → 1 にしたとき（2026-10-21）、副効果の合計が以前（3つ×0.3）と同じくらいになるよう subMult 0.3 → 0.9（BALANCE.md 30章）
     */
    mainSpan: 0.35,
    subMult: 0.9,
    /** 所持の上限（増えすぎて読み込みが重くならないように）。上限で拾ったら、いちばん弱いお守り（ロック中・装備中を除く）を売値の霊玉に換えて入れる（omamori.ts addOmamori） */
    inventory: 200,
    /**
     * 売値（霊玉）。旧課金品（paid）は売れない。2026-11-03 に銭（300/800/2000/5000）から霊玉に：式神Lv を育てやすく。
     * economy-sim（1日10分）で100面の日数は変わらず、式神Lv の平均は 3章 22→25・5章 32→36、全員Lv40 が62→35日目（BALANCE.md 47章）
     */
    sellReidama: { n: 1, r: 2, sr: 5, ssr: 10 } as Record<string, number>,
    /** 錬成：素材3つ＋銭（霊玉は式神Lv だけに使うので 霊玉20 → 銭。BALANCE.md 28章）。結果のレア度＝素材の平均（切り捨て）から [そのまま, +1, +2] の重み */
    fuseZeni: 2000,
    fuseShift: [50, 40, 10],
    /** 錬成で極になる重みの倍率（減らした分は素材と同じレア度へ）。優×3 で極 50% → 10% */
    fuseSsrMult: 0.2,
    /** 旧データの移行：主効果だけ・品質 q（課金で買った完成品は高め）・レア度 */
    legacyQ: 0.5,
    legacyPaidQ: 0.8,
  },
  login: {
    cycle: [
      { zeni: 1000 },
      { kuji: 2 },
      { zaimoku: 50 },
      { hyoro: 60 },
      { zeni: 3000 },
      { fuda: 1 },
      { kuji: 4 },
    ] as Partial<Record<'zeni' | 'reidama' | 'zaimoku' | 'hyoro' | 'fuda' | 'kuji' | 'exp', number>>[],
    milestoneEvery: 30,
    milestone: { kuji: 5 },
  },
  /**
   * オート周回（GAME_DESIGN 20章）：1戦の時間＝決着までの手番数×secPerTurn÷倍速（無料は×2、倍速パック speed4 の権利で×4）。
   * オートは Google 連携が要る（ゲストは使えない）ので、speedFree は「Google 連携の無料」の速さ（2026-11-03 に等速→×2。等速だと離脱の原因になるため）。サーバーは finish でこの時間が経ったかを見る。
   * 閉じてから（最後の finish から）graceMinutes 分は、次に開いたときにサーバーが計算して渡す
   */
  farm: {
    secPerTurn: 15,
    speedFree: 2,
    speedPaid: 4,
    graceMinutes: 10,
  },
  /** 寄進：全員の主EXP+expBonus を minutes 分、さらに全員に兵糧 hyoro（プレゼントボックス。hyoroHours 時間以内に受け取る）。2026-11-02 に兵糧を足し、100円 → 300円 */
  // hyoroPerDay：兵糧を配るのは1日（JST）にこの回数の寄進まで。20回で600（自然回復の約1.7倍）配られて序盤が進みすぎるので（2026-11-04、BALANCE.md 52章）
  kishin: { expBonus: 0.2, minutes: 30, dailyCap: 20, hyoro: 30, hyoroHours: 24, hyoroPerDay: 3 },
  paid: { exp2xMult: 2 },
  /** ゲスト（Google連携していない）で出撃できるのは第 maxChapter 章まで。先はデータを守るため連携が要る */
  guest: { maxChapter: 1 },
  hounou: { minZeni: 1000 },
  /** 推奨戦力の算出に使う「想定育成」（ステージ s のときの標準的なプレイヤー）。
   *  battle の敵の強さ（battle/config.ts の curve）はこの想定育成から作っている。ここを変えたら
   *  tools/sim の fit-curve → battle-sim --meta-power で較正し直す（Documentation/BALANCE.md） */
  standard: {
    /** 想定主Lv = levelA + levelB * (s-1)^levelP */
    levelA: 1,
    levelB: 2.64 * 9,
    levelP: 0.76,
    /** 想定主Lv の折れ線 [[面, Lv], …]（空でなければ上の式の代わりに使う。BALANCE.md 17章） */
    levelPoints: [[1, 1], [10, 28], [15, 91], [20, 181], [30, 379], [40, 550], [50, 685], [60, 775], [70, 793], [80, 802], [90, 811], [100, 820]] as [number, number][],
    /** 1章は 1 + earlySlope * (s-1) の直線。scaleFrom から earlyUntil までに上の曲線へ寄せる */
    earlySlope: 0.35 * 9,
    earlyUntil: 25,
    /** 想定の装備強化Lv = floor(enhA + enhB * (s-1))（+100の目盛り。旧 0.3 の5倍） */
    enhA: 0,
    enhB: 1.5,
    /** 想定の装備の段の折れ線 [[面, 段], …]（空なら章。BALANCE.md 17章） */
    gearTierPoints: [[60, 6], [100, 6.9]] as [number, number][],
    /** 想定で防具・根付を持っているステージ */
    armorFrom: 4,
    /** このステージからは装備がR */
    rareFrom: 11,
    /** このステージからは装備に銘（中くらいの値） */
    meiFrom: 6,
    /**
     * 想定で付けているお守り [このステージから, 種類, 段]。枠は1つなので、そのステージで当てはまる最後の1つ（2026-10-21）。
     * 以前（3枠）は 15面 陣取り2・35面 亀甲2・60面 城攻め3 を重ねていた。今は 15面から陣取り守（強化後）の段1だけ：
     * 推奨戦力は 15〜34面 +2%・35〜59面 -1%・60面〜 -2%（BALANCE.md 30章）
     */
    omamori: [[15, 'jintori', 1]] as [number, string, number][],
    /**
     * 想定育成の戦力に掛ける係数（銘・お守り・SRなど想定に入れていない分の補正）。
     * 1.2 → 1.15（2026-10-27）：基礎の速さを上げた分（アカリ・スズネ・ユズハ）で想定育成の4人の平均の戦力が +4.4% になったので、推奨戦力の数字が変わらないように（BALANCE.md 37章）
     * 1.15 → 1.135（2026-10-27）：速さの効果の見直し（羽団扇の固有・身軽・式神の速さ）で想定育成の戦力が 30面 +0.5%・50面〜 +1.4〜1.8% になった分（BALANCE.md 38章）
     */
    scale: 1.135,
    /** scale はステージ scaleFrom の1倍から scaleRamp ステージかけて上がる（1章は甘め） */
    scaleFrom: 10,
    /** ステージ1の係数（1章は手動で初見クリアできるように甘く） */
    easyStart: 0.85,
    scaleRamp: 20,
    netsukeFrom: 7,
    /** 想定の図面Lv = floor((s-1) / bpDiv) */
    bpDiv: 8,
    /**
     * 想定の式神（エリート・章ボスの30体）：エリートの★を (s - shikiEliteFrom) × shikiEliteStarsPerStage 個、
     * （式神Lv の導入（16.3）で 0.2・★4 まで → 0.35・★3 まで。★2 の常時効果・★3 の第二の能力が入り、持っている効果は Lv で決まるため、体数を増やした。BALANCE.md 16章）
     * その章までのエリートに図鑑番号の若い順で★shikiEliteFill まで配り、余れば★5まで。章ボス（レア・1%）は floor((s-1) × shikiBossPerStage) 体・★shikiStar。
     * 実際（economy-sim）はエリートの★がこの約4〜6倍集まる（2%×周回の撃破数）。以前の想定（小型・中型★1）と同じく控えめにして、
     * 100面到達の日数を据え置いた（実際に近い 0.9 にすると無課金105日・EXP2倍70日まで延びる。BALANCE.md 12章）
     */
    shikiEliteFrom: 14,
    shikiEliteStarsPerStage: 0.35,
    shikiEliteFill: 3,
    shikiBossPerStage: 0,
    shikiStar: 1,
    /** 想定の式神Lv = 1 + shikiLvPerStage × (s - shikiLvFrom)（★の上限まで。全員同じLv。BALANCE.md 16章） */
    shikiLvFrom: 20,
    shikiLvPerStage: 0.4,
    /** 想定の式神Lv の折れ線 [[面, Lv], …]（空でなければ上の直線の代わりに使う。BALANCE.md 17章） */
    shikiLvPoints: [[5, 1], [30, 10], [60, 20], [100, 28]] as [number, number][],
    /** 想定の式神（10種族）：その章までの種族のこの割合を持つ */
    shikiShare: 0.7,
    /** このステージからお供を付けている（持っている式神から戦力が最も高い1体） */
    companionFrom: 2,
    bossMult: 1.1,
    /**
     * 推奨戦力の章ごとの倍率（通常・章ボス）。推奨戦力ちょうどで勝率75%になるように battle-sim で合わせた
     * （--meta-power で章ごとに勝率75%の倍率を補間。2026-11-01、BALANCE.md 45・46章）。敵の倍率（battle の curveTune）を変えたら測り直す。
     * 地図で遊ぶ順番（5→7・8→6→9→10章）で推奨戦力が上がっていく
     */
    recTune: {
      normal: [0.81, 0.68, 0.73, 0.7, 0.78, 1.16, 0.83, 0.81, 1.11, 1.17] as number[],
      boss: [0.71, 0.7, 0.8, 0.69, 0.69, 1.16, 0.84, 0.92, 1.18, 1.2] as number[],
    },
    /** 章内の番号による上乗せ（index 1..10 で 0..0.09） */
    inChapterSlope: 0.01,
    /** 難易度が1つ上がるごとの推奨戦力の倍率（2026-11-04 に 1.4 → 1.2：敵側 CFG.difficulty はそのままで、推奨1.0倍の勝率が通常と同じ約78%になるように測り直した） */
    difficultyMult: 1.2,
  },
};

export type MetaConfig = typeof DEFAULT_CONFIG;
type DeepPartial<T> = { [K in keyof T]?: T[K] extends any[] ? T[K] : T[K] extends object ? DeepPartial<T[K]> : T[K] };

const clone = <T>(x: T): T => JSON.parse(JSON.stringify(x));

export const CONFIG: MetaConfig = clone(DEFAULT_CONFIG);
let version = 0;
export const configVersion = () => version;

function merge(target: any, patch: any) {
  for (const k of Object.keys(patch)) {
    const v = patch[k];
    if (v && typeof v === 'object' && !Array.isArray(v) && target[k] && typeof target[k] === 'object') merge(target[k], v);
    else target[k] = clone(v);
  }
}

/** 数値の一部を上書きする（シミュレーター・テスト用） */
export function configure(patch: DeepPartial<MetaConfig>) {
  merge(CONFIG, patch);
  version++;
}

export function resetConfig() {
  for (const k of Object.keys(CONFIG)) delete (CONFIG as any)[k];
  Object.assign(CONFIG, clone(DEFAULT_CONFIG));
  version++;
}
